import React, { memo, useState } from 'react';
import { useTheme } from 'styled-components';
import CheckBox from 'src/components/form/CheckBox';
import { Poppins } from 'src/common';
import _ from 'lodash';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { useAuth } from 'src/state/auth';
import * as S from './GTColumnSelectAnchor.styles';

interface GTColumnSelectAnchorProps {
  options: { key: string; title: string }[];
  value: string[];
  defaultOptions: string[];
  onChange: (value: string[]) => void;
  tableId: TableIds;
}

export const GTColumnSelectAnchor: React.FC<GTColumnSelectAnchorProps> = memo(
  ({ options, defaultOptions, onChange, value, tableId }) => {
    const colors = useTheme().colors;
    const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
    const { features } = useAuth();

    const handleSelect = (key: string) => {
      let values: string[] = [];
      if (value.includes(key)) {
        values = value.filter((el) => el !== key);
      } else {
        values = [...value, key];
      }
      onChange(values);

      mpEvent(MPEvents.TableColumnsChange, { columns: values });
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      setAnchorEl(e.currentTarget);
      e.stopPropagation();
    };

    const handleResetToDefault = () => {
      onChange(defaultOptions);
      localStorage.removeItem(`${tableId}-column-sizing`);
      const event = new CustomEvent('column-sizing-reset', { detail: { tableId } });
      window.dispatchEvent(event);
      setAnchorEl(null);
    };

    const isAllSelected = options.every((el) => value.includes(el.key));

    const ignoredColumns: string[] = [];

    return (
      <>
        <S.Div onClick={handleClick} $isOpen={!!anchorEl} data-cy="gt-csa">
          <svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 27 27" fill="none">
            <rect
              x="0.75"
              y="0.75"
              width="25.5"
              height="25.5"
              rx="1.75"
              fill={colors.cultured}
              stroke={colors.stroke}
              strokeWidth="0.5"
              id="p-bg"
            />
            <path
              d="M8.53564 8.08L8.53564 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
            <path
              d="M13.46 8.08L13.46 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
            <path
              d="M18.3843 8.08L18.3843 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
          </svg>
        </S.Div>

        <S.StyledMenu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          autoFocus={false}
          disableAutoFocusItem
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              setAnchorEl(null);
            }
          }}
        >
          <div className="gtcs-options">
            <div
              className="gtcs-options__item"
              onClick={() => {
                onChange(isAllSelected ? [] : options.map((el) => el.key));
              }}
            >
              <CheckBox isChecked={isAllSelected} small />
              <Poppins className="gtcs-options__item__title" nowrap>
                All
              </Poppins>
            </div>

            {options
              .filter((el) => !ignoredColumns.includes(el.key))
              .map((el) => (
                <div
                  className="gtcs-options__item"
                  data-cy={`gt-csa-${el.key}`}
                  key={el.key}
                  onClick={() => {
                    handleSelect(el.key);
                  }}
                >
                  <CheckBox isChecked={value.includes(el.key)} small />
                  <Poppins className="gtcs-options__item__title" nowrap>
                    {el.title}
                  </Poppins>
                </div>
              ))}

            {!!defaultOptions && (
              <div className="gtcs-options__clear" onClick={handleResetToDefault}>
                <Poppins>Reset to default</Poppins>
              </div>
            )}
          </div>
        </S.StyledMenu>
      </>
    );
  },
  (prev, next) => _.isEqual(prev, next),
);
